import { Action, createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { GaAuthState, UserSession } from './../models/index';

export class GaSessionState {
    public sessionUser: UserSession;
    public state: GaAuthState;
    public error: any;
}

export const sessionInitialState: GaSessionState = {
    sessionUser: null,
    state: GaAuthState.None,
    error: null
};

// ACTIONS
export enum SessionActionTypes {
    Restore = '[SESSION] Restore',
    RestoreSuccess = '[SESSION] Restore Success',
    RestoreFailure = '[SESSION] Restore Failure',
    Signin = '[SESSION] Signin',
    SigninAbort = '[SESSION] Signin Abort',
    SigninSuccess = '[SESSION] Signin Succes',
    SigninFailure = '[SESSION] Signin Failure',
    Signup = '[SESSION] Signup',
    SignupSuccess = '[SESSION] Signup Succes',
    SignupFailure = '[SESSION] Signup Failure',
    Signout = '[SESSION] Signout',
    SignoutSuccess = '[SESSION] Signout Succes',
    SignoutFailure = '[SESSION] Signout Failure'
}

export const restoreAction = createAction(SessionActionTypes.Restore);
export const restoreSuccessAction = createAction(SessionActionTypes.RestoreSuccess, props<{ sessionUser: UserSession }>());
export const restoreFailureAction = createAction(SessionActionTypes.RestoreFailure, props<{ error: any }>());

export const signinAction = createAction(SessionActionTypes.Signin, props<{ login: string, password: string }>());
export const signinAbortAction = createAction(SessionActionTypes.SigninAbort);
export const signinSuccessAction = createAction(SessionActionTypes.SigninSuccess, props<{ sessionUser: UserSession }>());
export const signinFailureAction = createAction(SessionActionTypes.SigninFailure, props<{ error: any }>());

export const signupAction = createAction(SessionActionTypes.Signup, props<{ user: UserSession }>());
export const signupSuccessAction = createAction(SessionActionTypes.SignupSuccess, props<{ sessionUser: UserSession }>());
export const signupFailureAction = createAction(SessionActionTypes.SignupFailure, props<{ error: any }>());

export const signoutAction = createAction(SessionActionTypes.Signout);
export const signoutSuccessAction = createAction(SessionActionTypes.SignoutSuccess);
export const signoutFailureAction = createAction(SessionActionTypes.SigninFailure, props<{ error: any }>());

// SELECTORS

export const getSessionStoreState = createFeatureSelector<GaSessionState>('auth');

export const getSessionUser = createSelector(getSessionStoreState, (state: GaSessionState) => state.sessionUser);
export const getSessionState = createSelector(getSessionStoreState, (state: GaSessionState) => state.state);
export const getSessionError = createSelector(getSessionStoreState, (state: GaSessionState) => state.error);
export const getSessionIsAuthenticated = createSelector(getSessionStoreState, (state) => state.state === GaAuthState.Authenticated);

// REDUCER

export const sessionReducerFn = createReducer<GaSessionState>(
    sessionInitialState,
    on(restoreAction, (state) =>
        ({ ...state, state: GaAuthState.Progress, error: null })),
    on(restoreSuccessAction, (state, { sessionUser }) =>
        ({ ...state, sessionUser, state: GaAuthState.Authenticated, error: null })),
    on(restoreFailureAction, (state, { error }) =>
        ({ ...state, state: GaAuthState.NonAuthenticated, error })),

    on(signinAction, (state) => ({ ...state, state: GaAuthState.Progress, error: null })),
    on(signinAbortAction, (state) =>
        ({ ...state, state: state.state === GaAuthState.Progress ? GaAuthState.NonAuthenticated : state.state })),
    on(signinSuccessAction, (state, { sessionUser }) =>
        ({ ...state, state: GaAuthState.Authenticated, sessionUser, error: null })),
    on(signinFailureAction, (state, { error }) => ({ ...state, state: GaAuthState.NonAuthenticated, sessionUser: null, error })),

    on(signupAction, (state) => ({ ...state, state: GaAuthState.Progress, error: null })),
    on(signupSuccessAction, (state, { sessionUser }) =>
        ({ ...state, state: GaAuthState.Authenticated, sessionUser, error: null })),
    on(signupFailureAction, (state, { error }) => ({ ...state, sessionUser: null, error })),

    on(signoutAction, (state) => ({ ...state, state: GaAuthState.Progress, error: null })),
    on(signoutSuccessAction, (state) => ({ ...state, state: GaAuthState.NonAuthenticated, sessionUser: null, error: null })),
    on(signoutFailureAction, (state, { error }) => ({ ...state, error }))
);

export function sessionReducer(state: GaSessionState | undefined, action: Action): GaSessionState {
    return sessionReducerFn(state, action);
}
